// Central content/mocks for the Pequeño Fresno clone

export const NAV = [
  { label: "Inicio", to: "/" },
  { label: "C.E.T.", to: "/cet" },
  { label: "Asociación", to: "/asociacion" },
  { label: "Eventos", to: "/eventos" },
  { label: "Acerca de", to: "/nosotros" },
  { label: "Contacto", to: "/contacto" },
];

export const ASSETS = {
  logo: "/logo.png",
  butterfly:
    "https://images.unsplash.com/photo-1517423738875-5ce310acd3da?w=400&auto=format&fit=crop",
  // Assets locales (servidas desde /public/assets, sin dependencia de dominios externos)
  butterflyLeaf: "/assets/butterflyLeaf.svg",
  leafDeco: "/assets/leafDeco.svg",
  smallLeaf: "/assets/smallLeaf.svg",
  butterflyIcon: "/assets/butterflyIcon.svg",
  lia: "/assets/lia.jpg",
  laura: "/assets/laura.jpg",
  family: "/assets/family.png",
  logoText: "/logo.png",
  aboutOrigin: "/assets/aboutOrigin.svg",
};

export const HOME_PEOPLE = [
  {
    id: "lia",
    name: "Lia",
    subtitle: "Guerrera, vive con C.E.T.",
    img: ASSETS.lia,
  },
  {
    id: "laura",
    name: "Laura",
    subtitle: "Leona, madre de Lia",
    img: ASSETS.laura,
  },
  {
    id: "nosotros",
    name: "Nosotros",
    subtitle: "Familia, amigos, simpatizantes",
    img: ASSETS.family,
  },
];

export const CET_SECTIONS = [
  {
    id: "page-0",
    title: "Presentación",
    subtitle: "Presentación de la enfermedad",
    body: `El Complejo de Esclerosis Tuberosa (CET) es una enfermedad genética rara que afecta aproximadamente a 1 de cada 6.000 recién nacidos. Su nombre proviene de los crecimientos anormales o "tubérculos" que suelen formarse en el cerebro y otros órganos. Estos tumores benignos pueden interferir en el funcionamiento normal de órganos como el corazón, riñones, piel, pulmones y ojos.`,
  },
  {
    id: "page-1",
    title: "Causas y Factores Genéticos",
    subtitle: "Causas y factores genéticos",
    body: `El CET es causado por mutaciones en los genes TSC1 o TSC2, que controlan el crecimiento celular. En el 70% de los casos, estas mutaciones son espontáneas, mientras que en el 30% son heredadas. Estos dos genes producen proteínas llamadas hamartina y tuberina, respectivamente. Estas proteínas son esenciales para regular el crecimiento y división celular.`,
    twoCols: [
      {
        heading: "Mutación en el gen TSC1:",
        text: "La mutación en este gen afecta la producción de la hamartina, lo que interrumpe la regulación normal del crecimiento celular. Esto puede provocar el desarrollo de tumores benignos en varios órganos del cuerpo.",
      },
      {
        heading: "Mutación en el gen TSC2:",
        text: "La mutación en el gen TSC2 afecta la producción de la tuberina, que también es responsable de controlar el crecimiento celular. Su alteración contribuye a la formación de tumores en distintos órganos.",
      },
    ],
    footer:
      "Ambas mutaciones generan un exceso de crecimiento celular en los tejidos afectados, lo que da lugar a los síntomas característicos del CET, como tumores benignos, epilepsia y otros problemas de salud.",
  },
  {
    id: "page-2",
    title: "Similitudes y diferencias",
    subtitle: "Similitudes y diferencias en las mutaciones",
    body: `Aunque las mutaciones en los genes TSC1 (hamartina) y TSC2 (tuberina) pueden provocar síntomas y complicaciones similares (ya que ambas afectan el mismo proceso celular: el control del crecimiento y la proliferación de células), las mutaciones en cada gen pueden tener algunas diferencias en la gravedad y en la distribución de los síntomas.`,
    twoCols: [
      {
        heading: "Similitudes:",
        list: [
          "Tumores en varios órganos: tanto TSC1 como TSC2 pueden provocar tumores benignos en cerebro, riñones (angiomiolipomas), piel (fibromas), ojos y corazón (rabdomiomas).",
          "Epilepsia: independientemente de la mutación genética, es posible sufrir epilepsia por tumores en el cerebro.",
          "Retrasos en el desarrollo y problemas cognitivos: ambas mutaciones pueden causar dificultades de aprendizaje.",
        ],
      },
      {
        heading: "Diferencias:",
        list: [
          "Gravedad y frecuencia: las mutaciones en TSC2 tienden a ser más graves y con mayor cantidad de tumores o progresión más rápida.",
          "TSC1 suele asociarse a formas menos severas en algunos casos, aunque puede variar.",
        ],
      },
    ],
    footer:
      "En resumen, aunque ambos tipos de mutación pueden provocar síntomas similares, la severidad y el tipo exacto de complicaciones pueden diferir ligeramente, siendo TSC2 la asociada a formas más graves.",
  },
  {
    id: "page-3",
    title: "Principales Síntomas",
    subtitle: "Principales síntomas",
    list: [
      "Neurológicos: Epilepsia, retrasos del desarrollo y dificultades de aprendizaje.",
      "Dermatológicos: Manchas hipopigmentadas o de hoja de fresno, fibromas ungueales y placas de piel gruesa.",
      "Renales: Angiomiolipomas, que pueden causar dolor o sangrado.",
      "Cardíacos y pulmonares: Presencia de rabdomiomas o linfangioleiomiomatosis.",
    ],
  },
  {
    id: "page-4",
    title: "Diagnóstico",
    subtitle: "Diagnóstico",
    body: `El diagnóstico se realiza combinando observaciones clínicas con pruebas como resonancias magnéticas, electroencefalogramas, tomografías, ecografías y estudios genéticos.`,
  },
  {
    id: "page-5",
    title: "Tratamientos y Manejo",
    subtitle: "Tratamientos y manejo",
    body: "Aunque no existe una cura definitiva, los tratamientos incluyen:",
    list: [
      "Medicación para controlar la epilepsia.",
      "Terapias dirigidas con inhibidores de mTOR (como el everolimus).",
      "Procedimientos quirúrgicos para tumores problemáticos.",
      "Terapias educativas y de apoyo psicológico.",
    ],
  },
  {
    id: "page-6",
    title: "Recursos",
    subtitle: "Recursos",
    intro: "Recopilamos herramientas y organizaciones útiles para familias, profesionales y personas afectadas por el CET.",
    resources: [
      {
        id: "tandem",
        name: "TANDem",
        summary:
          "Iniciativa desarrollada por expertos en neurología (Dra. Anna Jansen y otros) para abordar los trastornos neuropsiquiátricos asociados a la Esclerosis Tuberosa (TAND: Tuberous Sclerosis-Associated Neuropsychiatric Disorders): dificultades de aprendizaje, problemas conductuales, ansiedad, depresión y dificultades sociales del espectro autista.",
        highlights: [
          "TAND Checklist: cuestionario disponible en castellano para evaluar los síntomas en consulta.",
          "Recomendado a partir de los 2-3 años, revisable periódicamente en el seguimiento médico.",
          "Fomenta una red global de apoyo entre familias, cuidadores y especialistas.",
        ],
        links: [
          { label: "TAND Checklist (PDF)", url: "https://tandconsortium.org/wp-content/uploads/2021/01/1161_A4_4pp_TAND_Castilian_Spanish_update_WEB_v2.pdf" },
        ],
      },
      {
        id: "feder",
        name: "FEDER",
        summary:
          "Federación Española de Enfermedades Raras. Ofrece apoyo emocional, asesoramiento sobre recursos y derechos, impulso a la investigación y acceso a redes de ayuda. Colabora con entidades y profesionales para promover el diagnóstico temprano y mejorar la atención médica y social.",
        highlights: [],
        links: [
          { label: "Sitio web FEDER", url: "https://www.enfermedades-raras.org/" },
        ],
      },
      {
        id: "alce",
        name: "ALCE",
        summary:
          "Asociación de Epilepsia de la Comunidad Valenciana. Apoya a personas con epilepsia y a sus familias con información, orientación y acompañamiento. Para afectados por Esclerosis Tuberosa aporta asesoramiento sobre tratamientos, apoyo psicológico y recursos educativos y laborales.",
        highlights: [],
        links: [
          { label: "Sitio web ALCE", url: "http://www.alceepilepsia.org/" },
          { label: "Guía de Epilepsia", url: "https://www.alceepilepsia.org/guia-de-epilepsia/" },
        ],
      },
    ],
  },
];

export const UPCOMING_EVENTS = [
  {
    date: "21/06/2026",
    location: "Alicante",
    title: "¡Atentos a nuestro Instagram!",
    description: "Información sobre el C.E.T.",
    img: "https://pequenofresno.net/eventos/_assets/media/a4c38b526f3e5a7e471312aec1975909.jpg",
  },
];

export const PAST_EVENTS = [
  {
    date: "13/06/2026",
    title: "Pequeño fresno se suma al día solidario de Sant Joan d'Alacant",
    description:
      "A través de nuestro stand informativo, tuvimos la oportunidad de dar a conocer y sensibilizar al público sobre el Complejo de Esclerosis Tuberosa (CET), una enfermedad rara que afecta a numerosas familias. Queremos agradecer de corazón a la organización por brindarnos este espacio y a todas las personas que se acercaron a conocernos.",
    img: "https://pequenofresno.net/eventos/_assets/media/224d45e39db9e157dd9facb8be77b276.jpg",
  },
  {
    date: "16/05/2026",
    title: "EUREKA! un año más a nuestro lado",
    description:
      "Un año más, queremos expresar nuestro más sincero agradecimiento al equipo EUREKA! del AMPA de la Escuela Europea de Alicante por su compromiso y solidaridad. Gracias a las actividades organizadas durante este curso, EUREKA! ha realizado una generosa donación de 430 € a nuestra asociación.",
    img: "https://pequenofresno.net/eventos/_assets/media/c93898439e5bcf19c8b7d59c044dc825.jpg",
  },
  {
    date: "10/05/2026",
    title: "Un corazón inmenso",
    description:
      "Por tercer año consecutivo, Vanessa ha elegido celebrar su cumpleaños de una forma profundamente generosa: invitando a sus seres queridos a apoyar a Pequeño Fresno. Con este gesto tan especial, Vanessa demuestra una vez más una generosidad excepcional.",
    img: "https://pequenofresno.net/eventos/_assets/media/59de8b1114f53655cb8c9f141e356c35.jpg",
  },
  {
    date: "04/02/2026",
    title: "Charity Cup 2025 - FCOAMI",
    description:
      "FCOAMI renueva su apoyo a Pequeño Fresno en 2025. Como cada año, el club de fútbol de la EUIPO ha organizado su torneo solidario. El equipo vencedor ha decidido apoyar a Pequeño Fresno, donando 1.600 euros a nuestra asociación.",
    img: "https://pequenofresno.net/eventos/_assets/media/dd3caeb59556d65d9ff710b5f5bae590.jpg",
  },
  {
    date: "21/12/2025",
    title: "Un partido para Lia",
    description:
      "El Polideportivo de Sant Joan acogió un partido solidario de fútbol entre veteranos del Hércules y del Valencia en apoyo a la pequeña Lía. Gracias al Ayuntamiento de Sant Joan y a todos los equipos y personas que participaron.",
    img: "https://pequenofresno.net/eventos/_assets/media/6d8ef447af32cfd1facfc7e8fc5d2dec.jpg",
  },
  {
    date: "17/06/2025",
    title: "Llena de alegría y gratitud",
    description:
      "Por segundo año consecutivo, Vanessa ha elegido celebrar su cumpleaños de una forma profundamente generosa, invitando a sus personas queridas a apoyar a Pequeño Fresno en lugar de recibir regalos.",
    img: "https://pequenofresno.net/eventos/_assets/media/11dd2bb32e8a3b406baa7e1e734fb870.jpg",
  },
  {
    date: "15/05/2025",
    title: "Concierto benéfico de primavera",
    description:
      "Queremos agradecer de corazón a la Escuela Europea de Alicante por habernos abierto las puertas para dar visibilidad al Complejo de Esclerosis Tuberosa. Gracias a todos por ayudarnos a que nuestras pequeñas mariposas sigan volando.",
    img: "https://pequenofresno.net/eventos/_assets/media/8e564773cb78fbaa3daba4d20f185983.png",
  },
  {
    date: "10/05/2025",
    title: "EUREKA!",
    description:
      "Durante el Spring Festival de la Escuela Europea de Alicante, el equipo EUREKA! del AMPA montó su stand solidario. ¡Gracias al esfuerzo y compromiso del equipo, hemos recibido un generoso donativo de 255 €!",
    img: "https://pequenofresno.net/eventos/_assets/media/23ad3435672cb020f0f32d3f4e4b8d7e.jpg",
  },
  {
    date: "05/10/2024",
    title: "Jornada Residencia Ballesol",
    description:
      "La Residencia Ballesol Via Parque organizó una jornada especial. Durante el evento, se presentó oficialmente la asociación y se llevaron a cabo actividades para recaudar fondos para nuestra causa.",
    img: "https://pequenofresno.net/eventos/_assets/media/788149d1c65c1c096b42e2452c4a7415.jpg",
  },
  {
    date: "01/10/2024",
    title: "Charity Cup 2024 - FCOAMI",
    description:
      "Como cada año, FCOAMI, el club de fútbol de la EUIPO, organiza su torneo con fines benéficos. Gracias a su generosidad, nuestra asociación ha recibido 900 euros.",
    img: "https://pequenofresno.net/eventos/_assets/media/20ab912b7109991b281d809d59bc6659.jpg",
  },
  {
    date: "15/06/2024",
    title: "Caminata solidaria",
    description:
      "Todo comenzó con la iniciativa de Angel Domenech, que desde hace 10 años se impone un reto anual para dar visibilidad a enfermedades poco conocidas. Este año 2024, ha tocado dar algo de luz al síndrome KBG y al Complejo de Esclerosis Tuberosa.",
    img: "https://pequenofresno.net/eventos/_assets/media/d852a049969160b0e9dba42697526c27.png",
  },
  {
    date: "10/05/2024",
    title: "Un gesto que llena de esperanza",
    description:
      "Nuestro más sincero agradecimiento a Vanessa, quien con motivo de su cumpleaños decidió convertir su celebración en un acto de solidaridad, logrando reunir la increíble cantidad de 450 euros para nuestra asociación.",
    img: "https://pequenofresno.net/eventos/_assets/media/118904a2848dd6b1164c76d3a900f102.jpg",
  },
];
